import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  LogOut,
  Star,
  Store,
} from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Card } from '../../components/Card';
import { Logo } from '../../components/Logo';
import { cn } from '../../lib/cn';
import { useActiveBusiness } from '../../api/hooks';
import { useSession } from '../../stores/session';
import { useCurrentReseller } from '../../api/reseller';

type Membership = {
  kind: 'business' | 'reseller';
  id: string;
  name: string;
  subtitle: string;
  description: string;
  to: '/' | '/reseller';
  icon: typeof Building2;
};

/**
 * AccountChooser — shown after login when the current user has memberships in
 * both a business and a reseller. Lets them pick which workspace to enter.
 *
 * Edge case: if the user has only one membership we briefly explain and
 * redirect after 600ms. The mock always shows the chooser unless the URL is
 * `?single=true` so reviewers can preview the redirect path.
 */
export function AccountChooser() {
  const navigate = useNavigate();
  const { data: currentBusiness } = useActiveBusiness();
  const { data: currentReseller } = useCurrentReseller();
  const user = useSession((s) => s.user);

  // Build the list of memberships for the current user. In the mock we
  // assume the user has BOTH; the `?single=true` query forces the single-
  // membership branch for previewing.
  const isSingle =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('single') === 'true';

  const memberships: Membership[] = useMemo(() => {
    const list: Membership[] = [];
    if (currentBusiness) {
      list.push({
        kind: 'business',
        id: currentBusiness.id,
        name: currentBusiness.name,
        subtitle: currentBusiness.subtitle ?? '',
        description: 'Subscription, billing, POS access',
        to: '/',
        icon: Building2,
      });
    }
    if (!isSingle && currentReseller) {
      list.push({
        kind: 'reseller',
        id: currentReseller.id,
        name: currentReseller.name,
        subtitle: 'Partner workspace',
        description: 'Customers, packages, payouts, branding',
        to: '/reseller',
        icon: Store,
      });
    }
    return list;
  }, [isSingle, currentBusiness, currentReseller]);

  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Auto-redirect when only one membership is available.
  useEffect(() => {
    if (memberships.length !== 1) return undefined;
    const single = memberships[0]!;
    const t = window.setTimeout(() => {
      navigate({ to: single.to });
    }, 600);
    return () => window.clearTimeout(t);
  }, [memberships, navigate]);

  function pick(m: Membership) {
    navigate({ to: m.to });
  }

  function makeDefault(id: string, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDefaultId(id);
    setToast('Default updated');
    window.setTimeout(() => setToast(null), 1800);
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark relative flex items-center justify-center px-4 py-10">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
        <AuthThemeToggle />
      </div>
      <div className="w-full max-w-xl">
        <div className="flex flex-col items-center mb-8">
          <Logo className="w-10 h-10 rounded-xl" />
          <span className="mt-3 text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Possignin
          </span>
        </div>

        {memberships.length === 1 ? (
          <SingleMembershipNotice membership={memberships[0]!} />
        ) : (
          <Card className="p-6 sm:p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                Continue as…
              </h1>
              <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
                {user?.name}, your account is linked to multiple workspaces.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {memberships.map((m) => (
                <OptionCardLarge
                  key={`${m.kind}-${m.id}`}
                  membership={m}
                  isDefault={defaultId === `${m.kind}-${m.id}`}
                  onPick={() => pick(m)}
                  onSetDefault={(e) => makeDefault(`${m.kind}-${m.id}`, e)}
                />
              ))}
            </div>

            <div className="mt-7 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3 text-xs text-zinc-500 dark:text-zinc-400">
              <span>
                Signed in as{' '}
                <span className="mono text-zinc-700 dark:text-zinc-200">{user?.email}</span>
              </span>
              <Link
                to="/login"
                className="inline-flex items-center gap-1 hover:text-zinc-700 dark:hover:text-zinc-200"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </Link>
            </div>
          </Card>
        )}

        {/* Toast */}
        <div
          aria-live="polite"
          className={cn(
            'fixed left-1/2 -translate-x-1/2 bottom-8 transition pointer-events-none',
            toast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
          )}
        >
          {toast && (
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-lg">
              <CheckCircle2 className="w-4 h-4" />
              {toast}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Single-membership auto-redirect ─────────────────────────────────────── */

function SingleMembershipNotice({ membership }: { membership: Membership }) {
  const Icon = membership.icon;
  return (
    <Card className="p-6 sm:p-8 text-center">
      <span className="inline-flex w-12 h-12 grid place-items-center rounded-full bg-brand-subtle text-brand dark:bg-brand-dm-subtle/30 dark:text-brand-dm mx-auto mb-4">
        <Icon className="w-6 h-6" />
      </span>
      <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
        Only one workspace — taking you there…
      </h1>
      <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
        Redirecting you to <span className="font-medium text-zinc-800 dark:text-zinc-100">{membership.name}</span>.
      </p>
      <div className="mt-5 inline-flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
        <span className="w-2 h-2 rounded-full bg-brand dark:bg-brand-dm animate-pulse" aria-hidden="true" />
        Redirecting…
      </div>
      <div className="mt-6">
        <Link
          to={membership.to}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium bg-brand text-white shadow-xs hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition"
        >
          Continue now
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Card>
  );
}

/* ── Option card ─────────────────────────────────────────────────────────── */

function OptionCardLarge({
  membership,
  isDefault,
  onPick,
  onSetDefault,
}: {
  membership: Membership;
  isDefault: boolean;
  onPick: () => void;
  onSetDefault: (e: React.MouseEvent) => void;
}) {
  const Icon = membership.icon;
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        'group relative flex flex-col gap-3 p-5 rounded-xl border-2 text-left transition',
        'border-zinc-200 hover:border-brand hover:bg-brand-subtle/40',
        'dark:border-zinc-700 dark:hover:border-brand-dm dark:hover:bg-brand-dm-subtle/20',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:focus-visible:ring-brand-dm focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-950',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'w-10 h-10 grid place-items-center rounded-lg flex-shrink-0 transition',
            membership.kind === 'business'
              ? 'bg-zinc-100 text-zinc-700 group-hover:bg-brand group-hover:text-white dark:bg-zinc-800 dark:text-zinc-200 dark:group-hover:bg-brand-dm dark:group-hover:text-zinc-950'
              : 'bg-emerald-100 text-emerald-700 group-hover:bg-emerald-600 group-hover:text-white dark:bg-emerald-950/40 dark:text-emerald-300',
          )}
        >
          <Icon className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            {membership.kind === 'business' ? 'Business owner' : 'Reseller'}
          </div>
          <div className="mt-0.5 text-base font-semibold text-zinc-950 dark:text-zinc-50 truncate">
            {membership.name}
          </div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
            {membership.subtitle}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-brand dark:group-hover:text-brand-dm transition flex-shrink-0 mt-1" />
      </div>

      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
        {membership.description}
      </p>

      <div className="mt-1 pt-3 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
          {isDefault ? (
            <>
              <Star className="w-3 h-3 fill-current" />
              Default
            </>
          ) : (
            <span className="opacity-60">Not default</span>
          )}
        </span>
        <span
          role="button"
          tabIndex={-1}
          onClick={onSetDefault}
          className={cn(
            'text-[11px] font-medium underline-offset-2 hover:underline transition cursor-pointer',
            isDefault ? 'text-zinc-400 dark:text-zinc-500' : 'text-brand dark:text-brand-dm',
          )}
        >
          {isDefault ? 'Default' : 'Set default'}
        </span>
      </div>
    </button>
  );
}
